<template>
  <div>
    <ExperimentComponent experiment-key="rvz28661SimplifiedRtbFlow">
      <template #control>
        <div
          ref="guestsEl"
          :class="computedClasses"
        >
          <button
            :disabled="disabled"
            type="button"
            class="text-gray-500 px-4 py-2 text-left cursor-pointer w-full"
            :class="{ 'pl-0 pr-0': flush }"
            @click="openGuestPicker"
          >
            <span class="block text-sm">{{ t('guests') }}</span>
            <span class="font-semibold text-primary-950">{{ t('guestsValue', guestsCount) }}<template v-if="computedPets">&nbsp;{{ t('withPets').toLowerCase() }}</template></span>
          </button>
        </div>

        <ClientOnly v-if="!disabled">
          <TFloatingBox
            v-if="isLgBreakpoint"
            v-model:visible="showForm"
            :trigger-el="guestsEl"
            :distance="16"
            class="z-60"
          >
            <FormInputGuestsPicker
              :adults="bookingEstimateParams.adults"
              :children="bookingEstimateParams.children"
              :max-guests="rvGuests"
              @update:guests="(guests: Guests) => update(guests)"
            />

            <FormSearchInputPetsSwitch
              v-if="!hidePetsSwitch"
              :pets="bookingEstimateParams.pets"
              class="mt-3"
              @update:pets="(pets: boolean) => update({ pets })"
            />
          </TFloatingBox>

          <TModalCritical
            v-else
            v-model:visible="showForm"
            :title="t('guests')"
            :label-primary="t('actions.apply')"
            @action:primary="(dismiss) => dismiss()"
          >
            <FormInputGuestsPicker
              :adults="bookingEstimateParams.adults"
              :children="bookingEstimateParams.children"
              :max-guests="rvGuests"
              @update:guests="(guests: Guests) => update(guests)"
            />

            <FormSearchInputPetsSwitch
              v-if="!hidePetsSwitch"
              :pets="bookingEstimateParams.pets"
              class="mt-3"
              @update:pets="(pets: boolean) => update({ pets })"
            />
          </TModalCritical>

          <!-- Prevent flickering on page load -->
          <template #fallback />
        </ClientOnly>
      </template>

      <template #variant>
        <MoleculeDrillDown
          ref="guestsDrillDownEl"
          :opened="showForm && !isLgBreakpoint && !disabled"
          :title="t('guests')"
          :value="computedGuestsLabel"
          :placeholder="t('addGuests')"
          :disabled="disabled || hideEditButton"
          test-id="change-guests"
          @open="openGuestPicker"
          @close="closeGuestPicker"
        >
          <FormInputGuestsPicker
            :adults="bookingEstimateParams.adults"
            :children="bookingEstimateParams.children"
            :max-guests="rvGuests"
            @update:guests="(guests: Guests) => update(guests)"
          />

          <FormSearchInputPetsSwitch
            v-if="!hidePetsSwitch"
            :pets="bookingEstimateParams.pets"
            class="mt-3"
            @update:pets="(pets: boolean) => update({ pets })"
          />

          <div class="mt-auto flex justify-end">
            <TButton @click="closeGuestPicker">
              {{ t('actions.apply') }}
            </TButton>
          </div>
        </MoleculeDrillDown>

        <ClientOnly v-if="!disabled">
          <TFloatingBox
            v-if="isLgBreakpoint"
            v-model:visible="showForm"
            :trigger-el="guestsDrillDownEl?.$el"
            :distance="16"
           
            class="z-60"
          >
            <FormInputGuestsPicker
              :adults="bookingEstimateParams.adults"
              :children="bookingEstimateParams.children"
              :max-guests="rvGuests"
              @update:guests="(guests: Guests) => update(guests)"
            />

            <FormSearchInputPetsSwitch
              v-if="!hidePetsSwitch"
              :pets="bookingEstimateParams.pets"
              class="mt-3"
              @update:pets="(pets: boolean) => update({ pets })"
            />
          </TFloatingBox>

          <!-- Prevent flickering on page load -->
          <template #fallback />
        </ClientOnly>
      </template>
    </ExperimentComponent>
  </div>
</template>

<script setup lang="ts">
import type { BookingEstimateGuests } from '~/types'

type Guests = {
  adults: number
  children: number
}

const props = defineProps<{
  disabled?: boolean
  adults?: number
  children?: number
  pets?: boolean
  hidePetsSwitch?: boolean
  flush?: boolean
  hideEditButton?: boolean
}>()

const emit = defineEmits<{
  update: [guests: BookingEstimateGuests]
}>()

const { t } = useI18n()
const { isLgBreakpoint } = useBreakpoint()
const { rvGuests } = useRvDetails()
const { bookingEstimateParams, updateGuests } = useBookingEstimate()

const computedAdults = computed(() => props.adults || bookingEstimateParams.value.adults)
const computedChildren = computed(() => props.children || bookingEstimateParams.value.children)
const computedPets = computed(() => props.pets || bookingEstimateParams.value.pets)

const guestsCount = computed(() => {
  return computedAdults.value + computedChildren.value
})

const computedGuestsLabel = computed(() => {
  if (!guestsCount.value) {
    return
  }

  return `${t('adults', computedAdults.value)} · ${t('kids', computedChildren.value)} · ${computedPets.value ? t('withPets') : t('noPets')}`
})

const guestsEl = useTemplateRef('guestsEl')
const guestsDrillDownEl = useTemplateRef('guestsDrillDownEl')

const computedClasses = computed(() => [
  'border-1 border-gray-200',
  {
    'border-t-0 border-l-0 border-r-0': props.flush,
  },
])

const showForm = ref(false)

function openGuestPicker() {
  showForm.value = true
}

function closeGuestPicker() {
  showForm.value = false
}

function update({ adults, children, pets }: { adults?: number, children?: number, pets?: boolean }) {
  const newAdult = adults ?? bookingEstimateParams.value.adults
  const newChildren = children ?? bookingEstimateParams.value.children
  const newPets = pets ?? bookingEstimateParams.value.pets

  updateGuests({
    adults: newAdult,
    children: newChildren,
    pets: newPets,
  })

  emit('update', {
    adults: newAdult,
    children: newChildren,
    pets: newPets,
  })
}
</script>

<i18n lang="json">
{
  "en": {
    "guests": "Guests",
    "addGuests": "Add guests",
    "guestsValue": "1 guest | {n} guests",
    "adults": "1 adult | {n} adults",
    "kids": "No kids | 1 kid | {n} kids",
    "withPets": "With pets",
    "noPets": "No pets"
  },
  "fr": {
    "guests": "Invités",
    "addGuests": "Ajouter des invités",
    "guestsValue": "1 invité | {n} invités",
    "adults": "1 adulte | {n} adultes",
    "kids": "Sans enfant | 1 enfant | {n} enfants",
    "withPets": "Avec animaux",
    "noPets": "Sans animal"
  }
}
</i18n>
